import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, AlertCircleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function PricingPreview() {
  const webDesignFeatures = ['Up to 5 pages', 'Responsive mobile design', 'Enquiry form', 'Basic SEO setup'];
  const ecommerceFeatures = ['Up to 20 products included', 'PayPal/Stripe integration', 'Responsive shop layout', 'Priority email support'];
  const maintenanceFeatures = ['Hosting & domain management', 'Security monitoring', 'Monthly backups', 'Plugin/theme updates'];
  return <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-374">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-375">
        <div className="max-w-6xl mx-auto" data-id="element-376">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-377">
            <div className="inline-flex items-center gap-2 bg-[#0DC3DC]/10 px-4 py-2 rounded-full mb-4" data-id="element-378">
              <AlertCircleIcon size={20} className="text-[#0DC3DC]" data-id="element-379" />
              <span className="text-sm font-medium text-[#0DC3DC]" data-id="element-380">
                Starting prices shown below
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-381">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-382">
              Every website is built specifically around your business needs.
              Final pricing is tailored during your free consultation.
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-383">
            {/* Launch */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-[#212C34] rounded-lg p-6 border border-[#D4AF37] shadow-lg flex flex-col" data-id="element-384">
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-385">Launch</h3>
              <div className="mb-4" data-id="element-386">
                <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-387">£495</p>
              </div>

              <ul className="space-y-2 mb-6 flex-grow" data-id="element-388">
                {['Up to 4 pages', 'Brand-aligned design', 'Contact form', 'Mobile-first build', 'Basic SEO setup', 'Domain connection'].map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-389">
                    <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-390" />
                    <span data-id="element-391">{feature}</span>
                  </li>)}
              </ul>

              <Link to="/contact" className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 py-3 rounded-lg font-bold transition-all duration-300 text-center block" data-id="element-392">
                Get your free homepage design
              </Link>
            </motion.div>

            {/* Standard */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-[#212C34] rounded-lg p-6 border-2 border-[#0DC3DC] shadow-xl flex flex-col relative" data-id="element-393">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2" data-id="element-394">
                <span className="bg-[#0DC3DC] text-white px-4 py-1 rounded-full text-xs font-bold" data-id="element-395">
                  MOST POPULAR
                </span>
              </div>

              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-396">Standard</h3>
              <div className="mb-4" data-id="element-397">
                <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-398">£895</p>
              </div>

              <ul className="space-y-2 mb-6 flex-grow" data-id="element-399">
                {['Up to 8 pages', 'Custom design and build', 'Content integration', 'SEO-ready structure', 'Meta titles and descriptions', 'Favicon & social links', 'Full launch setup'].map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-400">
                    <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-401" />
                    <span data-id="element-402">{feature}</span>
                  </li>)}
              </ul>

              <Link to="/contact" className="w-full bg-[#0DC3DC] hover:bg-[#26D0E8] text-white py-3 rounded-lg font-bold transition-all duration-300 text-center block" data-id="element-403">
                Get your free homepage design
              </Link>
            </motion.div>

            {/* Growth */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-[#212C34] rounded-lg p-6 border border-[#D4AF37] shadow-lg flex flex-col" data-id="element-404">
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-405">Growth</h3>
              <div className="mb-4" data-id="element-406">
                <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-407">£1,395</p>
              </div>

              <ul className="space-y-2 mb-6 flex-grow" data-id="element-408">
                {['Up to 10 pages', 'Everything in Standard', 'Keyword-mapped page structure', 'Google Business Profile setup'].map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-409">
                    <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-410" />
                    <span data-id="element-411">{feature}</span>
                  </li>)}
              </ul>

              <Link to="/contact" className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 py-3 rounded-lg font-bold transition-all duration-300 text-center block" data-id="element-412">
                Get your free homepage design
              </Link>
            </motion.div>
          </div>

          <div className="mb-8 bg-[#0DC3DC]/10 border-l-4 border-[#0DC3DC] p-6 rounded-lg text-center" data-id="element-413">
            <p className="text-gray-900 dark:text-white font-medium" data-id="element-414">
              E-commerce and bespoke builds quoted individually — from £990.
              Additional pages £75 each.
            </p>
          </div>

          {/* Highlight Box */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-[#0DC3DC]/10 border-l-4 border-[#0DC3DC] p-6 rounded-lg mb-8" data-id="element-415">
            <p className="text-gray-900 dark:text-white font-medium text-center" data-id="element-416">
              <strong data-id="element-417">
                Every website is built specifically around your business needs.
              </strong>{' '}
              A tailored package is created after your free consultation.
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center" data-id="element-418">
            <Link to="/pricing" className="inline-block bg-[#0DC3DC] hover:bg-[#26D0E8] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-419">
              View Full Pricing & Book Free Consultation
            </Link>
          </motion.div>
        </div>
      </div>
    </section>;
}